import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Mail, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { trackNewsletterSubscribe } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
    const { t } = useI18n();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) {
            toast.error(t("newsletter.errorMissing"));
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/subscribe`, { name: name.trim(), email: email.trim() });
            setDone(true);
            trackNewsletterSubscribe();
            toast.success(t("newsletter.success"));
            setName("");
            setEmail("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                (Array.isArray(detail) && detail[0]?.msg) ||
                (typeof detail === "string" ? detail : "") ||
                t("newsletter.errorGeneric");
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section data-testid="newsletter-section" className="bg-[#0F172A] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold tracking-wider uppercase">
                        <Mail className="h-3.5 w-3.5" />
                        {t("newsletter.eyebrow")}
                    </div>
                    <h2 className="font-display mt-4 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold">
                        {t("newsletter.titleLine1")}
                        <span className="text-[#10B981]">{t("newsletter.titleHighlight")}</span>
                    </h2>
                    <p className="mt-3 text-slate-300 leading-relaxed max-w-lg">{t("newsletter.intro")}</p>
                </div>

                <form
                    data-testid="newsletter-form"
                    onSubmit={submit}
                    className="rounded-xl bg-white/5 border border-white/10 p-5 backdrop-blur"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                            data-testid="newsletter-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("newsletter.name")}
                            className="h-12 bg-white text-slate-900 border-transparent placeholder:text-slate-400 focus-visible:ring-[#10B981]"
                        />
                        <Input
                            data-testid="newsletter-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("newsletter.email")}
                            className="h-12 bg-white text-slate-900 border-transparent placeholder:text-slate-400 focus-visible:ring-[#10B981]"
                        />
                    </div>
                    <Button
                        data-testid="newsletter-submit"
                        type="submit"
                        disabled={loading}
                        className="mt-3 h-12 w-full bg-[#10B981] hover:bg-emerald-600 text-white font-semibold"
                    >
                        {loading ? t("newsletter.subscribing") : done ? t("newsletter.subscribed") : t("newsletter.submit")}
                        {!loading && !done && <ArrowRight className="h-4 w-4 ml-1.5" />}
                    </Button>
                    <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">{t("newsletter.disclaimer")}</p>
                </form>
            </div>
        </section>
    );
}
